import { Fragment, memo, useContext, useEffect, useRef, useState } from "react";
import { Table as BootstrapTable, Button } from "react-bootstrap";
import { BsSave, BsTrash } from "react-icons/bs";
import { FaCheck } from "react-icons/fa";
import { LiaPlusSolid } from "react-icons/lia";

import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import { IDataType } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, postMultiple } from "../../../shared/utils/api";
import Checkbox from "../checkbox/checkbox";
import "./editable-data-table.scss";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import Popup from "../popup/popup";
import { IDataUrl } from "../../utils";

export interface IAnyString {
  [index: string]: string;
}

export interface ITableRowData {
  id: string;
  [index: string]: any; // eslint-disable-line
}

export interface IColumn {
  key: string;
  displayName: string;
  dataType: IDataType;
  sortable?: boolean;
}

export interface ITableConfig {
  columns: IColumn[];
  data?: ITableRowData[];
  dataUrl?: IDataUrl;
  postUrl?: IDataUrl;
  defaultSortCol?: string;
  forceSave?: boolean;
  onSaveClicked?: (data: ITableRowData[]) => void;
  handleDataChange?: (isDirty: boolean) => void;
  setForceSave?: (value: boolean) => void;
}

const EditableDataTable = ({
  columns,
  data: tableData,
  dataUrl,
  postUrl,
  defaultSortCol, 
  onSaveClicked,
  handleDataChange,
  forceSave,
  setForceSave
}: ITableConfig) => {
  const [checkAll, setCheckAll] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [data, setData] = useState<ITableRowData[]>([]);
  const [currentSelectedRow, setCurrentSelectedRow] = useState<ITableRowData | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showNewPopup, setShowNewPopup] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const authCtx = useContext(AuthContext);

  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, false, authCtx.token, dataUrl?.transformType, dataUrl?.backend);

  const { sendRequest: postData, status: postDataStatus, data: postDataResult, error: postDataError } = useHttp(postMultiple, false, authCtx.token, postUrl?.transformType, postUrl?.backend);

  const node = useRef<any>(); // eslint-disable-line

  const parseDataUrl = (url: string) => {
    if (!dataUrl) {
      return url;
    }

    let queryString = "";
    if (defaultSortCol) {
      queryString = `?order_by=${defaultSortCol} asc`;
    }
    if (dataUrl.queryString) {
      if (queryString === "") {
        queryString = `?${dataUrl.queryString}`;
      } else {
        queryString = `${queryString}&${dataUrl.queryString}`;
      }
    }
    return `${url}${queryString}`;
  };

  useEffect(() => {
    if (dataUrl) {
      getTableData(parseDataUrl(dataUrl.url));
    } else if (tableData) {
      setData(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    if (isDirty) {
      handleDataChange?.(isDirty);
    }
  }, [isDirty]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError) {
      setData(getTableDataResult);
    }
  }, [getTableDataStatus]);

  useEffect(() => {
    if (postDataStatus === "completed" && !postDataError) {
      setData(postDataResult);
    }
  }, [postDataStatus]);

  const handleOnSelectRow = (id: string) => {
    const result = [...selectedRows];
    
    if (result.indexOf(id) === -1) {
      // not found=>add
      result.push(id);

      setSelectedRows(result);
    } else {
      const filteredListOfIds = result.filter((el) => el !== id);
      setSelectedRows(filteredListOfIds);
    }
  };

  const handleOnSelectAllRows = () => {
    if (data && !checkAll) {
      const result = data.map((row) => row.id);

      setSelectedRows(result);
      setCheckAll(true);
    } else {
      setSelectedRows([]);
      setCheckAll(false);
    }
  };

  const isSelected = (id:string) => {
    return selectedRows.indexOf(id) !== -1;
  };

  const handleOnRowClick = (row: ITableRowData) => {
    setCurrentSelectedRow(row);
    setShowNewPopup(true);
    setIsDirty(true);
  };

  const renderRows = () => {
    if (data.length === 0) {
      return <tr className="dataTable-row dataTable-rowNoData"><td colSpan={columns.length + 1}><span>No data to be displayed...</span></td></tr>;
    }
    const result = data.filter(row => {
      const columnsKeys = columns.map(column => column.key);
      let resultFilteredForMatching:IAnyString = {};
      let concatResultFilteredForMatching = "";

      columnsKeys.forEach(key => {
        resultFilteredForMatching = {...resultFilteredForMatching, [key] : row[key]};
      });
      for (const key in resultFilteredForMatching) {
        concatResultFilteredForMatching = concatResultFilteredForMatching.toString().toLowerCase() + " " + (resultFilteredForMatching[key]? resultFilteredForMatching[key].toString().toLowerCase() : "");
      }

      return concatResultFilteredForMatching.indexOf(searchInputValue.toLowerCase()) > -1;
    });
    
    return result.map((rowData: ITableRowData, i: number) => {
      return (
        <tr
          className="editableDataTable-row"
          key={i}
          onClick={() => handleOnRowClick(rowData)}
        >
          <td key={rowData.id}>
            <span>
              <Checkbox checked={isSelected(rowData.id)} onClick={() => handleOnSelectRow(rowData.id)} />
            </span>
          </td>
          {columns.map((column) => {
            let columnData = rowData[column.key];
            if (columnData === true) {
              columnData = (
                <Fragment>
                  <FaCheck className="editableDataTable-activeIcon" />
                </Fragment>
              );
            } 
            else if (columnData === false) {
              columnData = (
                <Fragment></Fragment>
              );
            }
            return <td className="standardNoHighlight" key={column.key}><span>{columnData}</span></td>;
          })}
        </tr>
      );
    });
  };

  const handlePopupClosed = (result: ITableRowData | undefined) => {
    if (result && currentSelectedRow?.id) {
      // edit
      const editedRowData = data.map(obj => {
        if (obj.id === result.id) {
          return result;
        }
        return obj;
      });

      setData(editedRowData);
    } else if (result) {
      // create new
      data.push(result);
      setData(data);
      setIsDirty(true);
    }

    setCurrentSelectedRow(undefined);
    setShowNewPopup(false);
  };

  const handleOnDelete = () => {
    setIsDirty(true);
    if (!selectedRows) {
      return;
    }

    const newData = data.filter(d => !selectedRows.some(row => row == d.id));
    setSelectedRows([]);
    setData(newData);
  };

  const handleSaveClick = () => {
    if (onSaveClicked) {
      onSaveClicked(data);
      return;
    }
    if (!postUrl) {
      return;
    }
    postData(parseDataUrl(postUrl.url), data);
    setIsDirty(false);
    handleDataChange?.(false);
  };

  useEffect(() => {
    if (forceSave) {
      handleSaveClick();
      setForceSave?.(false);
    }
  }, [forceSave]);

  return (
    <div className="editableDataTable">
      {[getTableDataStatus].some(status => status === "pending") && <Spinner />}
      <Popup
        mode={currentSelectedRow?.id ? "Edit" : "New"}
        buttonText={currentSelectedRow?.id ? "Save changes" : "Add"}
        columns={[...columns]}
        values={currentSelectedRow}
        show={showNewPopup}
        handlePopupClosed={handlePopupClosed}
      />
      <div className="editableDataTable-buttonsGroup">
        <TextInput
          type="text"
          id="editableDataTable-searchInput"
          required={false}
          inputKey="editableDataTable-searchInput"
          autofocus={false}
          placeholder="Type to filter"
          handleOnChange={(targetVal) => setSearchInputValue(targetVal)}
        />
        <Tooltip content="Delete" direction="top" delay={350} >
          <Button name="delete" disabled={selectedRows.length === 0} onClick={() => handleOnDelete()} className="editableDataTable-delete" variant="primary">
            <BsTrash />
          </Button>
        </Tooltip>
        <Tooltip content="Add a new field" direction="top" delay={350}>
          <Button 
            name="add"
            disabled={validateLoadingStatusNew([getTableDataStatus])}
            className="editableDataTable-new" 
            onClick={() => {setShowNewPopup(true); setIsDirty(true);}}>
            <LiaPlusSolid />
          </Button>
        </Tooltip>
        {(postUrl || onSaveClicked) && (
          <Tooltip content={"Save"} direction="top" delay={350} >
            <Button
              name="save"
              className="editableTable-save" onClick={handleSaveClick}>
              <BsSave />
            </Button>
          </Tooltip>
        )}
      </div>
      <div ref={node} className="editableDataTable-container">
        <BootstrapTable className="editableDataTable-table" size="sm" borderless striped>
          <thead className="editableDataTable-header">
            <tr>
              <th className="editableDataTable-actionTh" key="actions">
                <span>
                  <Checkbox checked={selectedRows.length === data.length} onClick={handleOnSelectAllRows} />
                </span>
              </th>
              {columns.map((column) => (
                <th key={column.key}>
                  <span>
                    {column.displayName}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </BootstrapTable>
      </div>
    </div>
  );
};

export default memo(EditableDataTable);
